import { Injectable } from '@angular/core';
import { forkJoin, Observable, of, throwError } from 'rxjs';
import { map, catchError, switchMap } from 'rxjs/operators';
import moment from 'moment';

import { AnalyticsService } from '../../../shared/services/analytics.service';
import { ProjectV2ServiceService } from '../../../shared/services/project-v2-service.service';
import { OverlayService } from '../../../shared/services/overlayService';
import { NotificationService } from '../../../shared/services/notification.service';

import {
  CAMPAIGN_WORKSPACE_CONSTANTS,
  CAMPAIGN_WORKSPACE_TRANSLATIONS,
} from '../../../shared/models/campaign-workspace-translations';
import { ErrorValue } from '../../../shared/models';
import { TranslatePipe } from '../../../shared/pipes/translate.pipe';

@Injectable({
  providedIn: 'root',
})
export class CampaignDriveService {
  constructor(
    private readonly analyticService: AnalyticsService,
    private readonly projectV2Service: ProjectV2ServiceService,
    private readonly notificationService: NotificationService,
    private readonly overlayService: OverlayService,
    private readonly translatePipe: TranslatePipe
  ) {}

  selectCalendarMode(mode: string): { dates: { startDate: string; endDate: string }; viewMode: string } {
    const dates = this.projectV2Service.calculateDateRange(mode);

    let viewMode = CAMPAIGN_WORKSPACE_CONSTANTS.CALENDAR_VIEW_MODES.LAST_UPDATED;
    switch (mode) {
      case CAMPAIGN_WORKSPACE_CONSTANTS.CALENDAR_MODE_VALUES.THREE_MONTHS:
        viewMode = CAMPAIGN_WORKSPACE_CONSTANTS.CALENDAR_VIEW_MODES.THREE_MONTHS;
        break;
      case CAMPAIGN_WORKSPACE_CONSTANTS.CALENDAR_MODE_VALUES.SIX_MONTHS:
        viewMode = CAMPAIGN_WORKSPACE_CONSTANTS.CALENDAR_VIEW_MODES.SIX_MONTHS;
        break;
      case CAMPAIGN_WORKSPACE_CONSTANTS.CALENDAR_MODE_VALUES.WEEK:
        viewMode = CAMPAIGN_WORKSPACE_CONSTANTS.CALENDAR_VIEW_MODES.WEEK;
        break;
    }

    return { dates, viewMode };
  }

  sortCampaign(campaigns: any[], mode: string): any[] {
    return [...campaigns].sort((a, b) => {
      if (mode === CAMPAIGN_WORKSPACE_TRANSLATIONS.STATUS_MODE) {
        return b.activeProduct.localeCompare(a.activeProduct);
      } else if (mode === CAMPAIGN_WORKSPACE_TRANSLATIONS.ALPHA_MODE) {
        return a.name.localeCompare(b.name);
      } else {
        return a.expectedMad - b.expectedMad > 0 ? -1 : 1;
      }
    });
  }

  deleteDriveCampaign(item: any): Observable<boolean | undefined> {
    return this.notificationService
      .open({
        notificationType: 'danger',
        centerHeader: true,
        question: this.translatePipe.transform(CAMPAIGN_WORKSPACE_TRANSLATIONS.DRIVE_CAMPAIGN_DELETE_CONFIRM),
        question2: this.translatePipe.transform(CAMPAIGN_WORKSPACE_TRANSLATIONS.DRIVE_CAMPAIGN_DELETE_CONFIRM_QUESTION),
        confirmBtn: this.translatePipe.transform(CAMPAIGN_WORKSPACE_TRANSLATIONS.YES),
        cancelBtn: this.translatePipe.transform(CAMPAIGN_WORKSPACE_TRANSLATIONS.NO),
      })
      .pipe(
        switchMap((confirm) => {
          if (!confirm) return of(undefined);
          return this.projectV2Service.deleteDriveCampaign(item.workspaceId, item.id).pipe(
            map(() => true),
            catchError((err) => {
              if (err?.errorCode === CAMPAIGN_WORKSPACE_TRANSLATIONS.INVALID_DELETE_CAMPAIGN_OPERATION) {
                this.overlayService.showError(
                  this.translatePipe.transform(CAMPAIGN_WORKSPACE_TRANSLATIONS.DRIVE_CAMPAIGN_ERROR_HAS_PROJECT)
                );
              } else {
                this.overlayService.showError(
                  this.translatePipe.transform(CAMPAIGN_WORKSPACE_TRANSLATIONS.DRIVE_CAMPAIGN_ERROR)
                );
              }
              return throwError(() => err);
            })
          );
        })
      );
  }

  searchDriveCampaigns(searchTerm: string, backupDriveCampaigns: any[]): any[] {
    if (!searchTerm) {
      return backupDriveCampaigns;
    }

    const lowerSearch = searchTerm.toLowerCase();

    return backupDriveCampaigns.filter((item) => {
      if (!item.name) return false;
      return item.name.toLowerCase().includes(lowerSearch);
    });
  }

  getFavoriteItems(eventType: any, params: any): Observable<any[]> {
    return this.analyticService.getFavoriteItem(eventType, params);
  }

  processFavMode(
    res: any[],
    opts: { workspaceId?: string | null; cacheBuster: string; defaultActiveProductLabel: string }
  ): { drive: any[]; backup: any[] } {
    const filtered = opts.workspaceId ? res.filter((c) => c.workspaceId === opts.workspaceId) : [...res];

    const drive = filtered
      .map((p) => ({
        ...p,
        isFavorite: true,
        logo: p.logo ? `${p.logo}?v=${opts.cacheBuster}` : null,
        activeProduct: p.activeProduct || opts.defaultActiveProductLabel,
      }))
      .sort((a, b) => a.name.localeCompare(b.name));

    const backup = filtered
      .map((p) => ({
        ...p,
        isFavorite: true,
        logo: p.logo ? `${p.logo}?v=${opts.cacheBuster}` : null,
      }))
      .sort((a, b) => a.name.localeCompare(b.name));

    return { drive, backup };
  }

  processAllMode(
    res: any[],
    lists: { favDriveCampaigns: any[]; favBackupDriveCampaigns: any[] }
  ): { drive: any[]; backup: any[] } {
    const drive = this.updateFavoriteFlags(lists.favDriveCampaigns, res);
    const backup = this.updateFavoriteFlags(lists.favBackupDriveCampaigns, res);
    return { drive, backup };
  }

  processArchivedMode(
    res: any[],
    lists: { archiveDriveCampaigns: any[]; archiveBackupDriveCampaigns: any[] }
  ): { drive: any[]; backup: any[] } {
    const drive = this.updateFavoriteFlags(lists.archiveDriveCampaigns, res);
    const backup = this.updateFavoriteFlags(lists.archiveBackupDriveCampaigns, res);
    return { drive, backup };
  }

  private updateFavoriteFlags(list: any[], res: any[]): any[] {
    const resIds = new Set(res.map((r) => r.id));
    const flagged = list.map((item) => ({ ...item, isFavorite: resIds.has(item.id) }));
    return this.sortByName(this.dedupeById(flagged));
  }

  private dedupeById(list: any[]): any[] {
    const seen = new Set<string>();
    const out: any[] = [];
    for (const item of list) {
      const { id } = item;
      if (!seen.has(id)) {
        seen.add(id);
        out.push(item);
      }
    }
    return out;
  }

  private sortByName(list: any[]): any[] {
    return [...list].sort((a, b) => a.name.localeCompare(b.name));
  }

  mergeCampaignPages(existing: any[], nextPage: any[]): any[] {
    return this.sortByName(this.dedupeById([...existing, ...nextPage]));
  }

  getWorkspaceDriveCampaign(
    workspaceId: string,
    isAll: boolean,
    isArchived: boolean,
    campaignSearchParams: any,
    calendarViewDates: any,
    pagingState: any,
    firstInit: boolean,
    cacheBuster: string,
    months: string[],
    translations: any
  ): Observable<any> {
    let archived = false;
    if (isAll) archived = false;
    else if (isArchived) archived = true;

    const requests: Observable<any>[] = [
      this.projectV2Service.getDriveCampaign(
        workspaceId || '',
        archived,
        campaignSearchParams,
        calendarViewDates ?? null,
        pagingState
      ),
    ];
    if (firstInit) {
      requests.push(
        this.projectV2Service.getDriveCampaign(
          workspaceId || '',
          archived,
          null,
          calendarViewDates ?? null,
          pagingState
        )
      );
    }

    return forkJoin(requests).pipe();
  }

  processWorkspaceResult(
    result: any[],
    firstInit: boolean,
    cacheBuster: string,
    months: string[],
    translations: any,
    archived: boolean,
    yearsRef: number[],
    madMonthYear: any,
    categoryTags: string[]
  ) {
    if (firstInit && result[1]) {
      const dates = result[1].data.map((d: any) => d.expectedMad);
      const max = moment.unix(Math.max(...dates) / 1000).year();
      const min = moment.unix(Math.min(...dates) / 1000).year();
      yearsRef.length = 0;
      for (let i = min; i <= max; i++) {
        yearsRef.push(i);
        madMonthYear[i] = {};
        for (const m of months) {
          madMonthYear[i][m] = false;
        }
      }
    }

    const { hasNext } = result[0];
    const pagingState = result[0].pagingState ?? null;

    const campaigns = [...result[0].data];
    const backup = [...result[0].data];

    campaigns.forEach((drives) => {
      drives.isFavorite = false;
      if (drives.logo) drives.logo = drives.logo + `?v=${cacheBuster}`;
      if (drives.name) categoryTags.push(drives.name);
      if (!drives.activeProduct) drives.activeProduct = translations.YES;
      if (drives.activeProduct) {
        drives.activeProduct =
          drives.activeProduct[0].toUpperCase() + drives.activeProduct.slice(1).toLocaleLowerCase();
      }
    });

    campaigns.sort((a, b) => a.name.localeCompare(b.name));
    backup.sort((a, b) => a.name.localeCompare(b.name));

    const favDriveCampaigns = campaigns;
    const favBackupDriveCampaigns = backup;

    const archiveDriveCampaigns = archived ? campaigns : [];
    const archiveBackupDriveCampaigns = archived ? backup : [];

    return {
      hasNext,
      pagingState,
      campaigns,
      backup,
      favDriveCampaigns,
      favBackupDriveCampaigns,
      archiveDriveCampaigns,
      archiveBackupDriveCampaigns,
    };
  }

  archiveDriveCampaign(item: any, driveCampaigns: any[]): Observable<any[]> {
    const originalArchivedState = item.archived;
    const targetArchivedState = !item.archived;
    item.isArchiving = true;

    return this.projectV2Service
      .updateDriveCampaign({ ...item, archived: targetArchivedState, isArchived: targetArchivedState })
      .pipe(
        map((response: any) => {
          item.archived = response.archived;
          item.isArchived = response.isArchived;
          item.isArchiving = false;
          return driveCampaigns.filter((drive) => drive.id !== item.id);
        }),
        catchError((err) => {
          item.archived = originalArchivedState;
          item.isArchiving = false;
          throw err;
        })
      );
  }
}
